import { Link } from "react-router-dom";
import { Scale, AlertCircle, CheckCircle, FileText, Clock, Shield, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  'headline': 'Rechtliche Schritte bei Mobbing: Anwalt, Klage, Schadensersatz',
  'description': 'Welche rechtlichen Schritte sind bei Mobbing möglich? Unterlassung, Schadensersatz, Kündigung anfechten. Wann lohnt sich ein Anwalt für Arbeitsrecht?',
  'author': { '@type': 'Organization', 'name': 'mobbing-melden.de' },
  'publisher': { '@type': 'Organization', 'name': 'mobbing-melden.de', 'url': 'https://www.mobbing-melden.de' },
  'inLanguage': 'de-DE',
};

const LEGAL_OPTIONS = [
  {
    emoji: "🛑",
    color: "#FFC4D6",
    title: "Unterlassungsklage",
    when: "Wenn das Mobbing aktiv andauert",
    text: "Du kannst vor dem Arbeitsgericht eine einstweilige Verfügung auf Unterlassung beantragen — der Mobber wird gerichtlich verpflichtet, das Verhalten zu stoppen.",
    fast: true,
  },
  {
    emoji: "💶",
    color: "#A0E8AF",
    title: "Schadensersatz & Schmerzensgeld",
    when: "Bei nachweisbaren Schäden (Gesundheit, Karriere)",
    text: "Wenn durch Mobbing Gesundheitsschäden, Verdienstausfälle oder psychische Belastungen entstanden sind, kann Schadensersatz gefordert werden (§§ 823, 253 BGB).",
    fast: false,
  },
  {
    emoji: "📋",
    color: "#BDE0FE",
    title: "Änderungskündigung anfechten",
    when: "Wenn du versetzt oder degradiert wirst",
    text: "Setzt der Arbeitgeber eine Versetzung als Mobbing-Mittel ein, kann diese vor dem Arbeitsgericht angefochten werden. Frist: 3 Wochen nach Zugang der Kündigung.",
    fast: true,
  },
  {
    emoji: "🏥",
    color: "#F6E05E",
    title: "Krankmeldung & Lohnfortzahlung",
    when: "Bei mobbing-bedingten Erkrankungen",
    text: "Bei einer ärztlich attestierten Erkrankung durch Mobbing (Burnout, Depression) besteht Anspruch auf Lohnfortzahlung für 6 Wochen — danach Krankengeld.",
    fast: false,
  },
];

const STEPS = [
  {
    number: 1,
    color: "#A0E8AF",
    title: "Dokumentation sicherstellen",
    text: "Ohne Beweise kein Prozess. Führe ein lückenloses Protokoll, sichere Screenshots und Zeugen, bevor du rechtliche Schritte einleitest.",
    link: { label: "Protokoll erstellen →", path: "/mobbing-protokoll" }
  },
  {
    number: 2,
    color: "#BDE0FE",
    title: "Innerbetrieblich eskalieren",
    text: "Beschwere dich zuerst beim Betriebsrat und melde den Fall dem Arbeitgeber schriftlich. Das ist Voraussetzung für spätere Klagen.",
    link: { label: "Betriebsrat einschalten →", path: "/betriebsrat" }
  },
  {
    number: 3,
    color: "#FFC4D6",
    title: "Anwalt für Arbeitsrecht kontaktieren",
    text: "Hole dir eine arbeitsrechtliche Erstberatung. Viele Anwälte bieten diese kostenlos oder günstig an — Gewerkschaftsmitglieder haben oft kostenlosen Rechtsschutz.",
    link: null
  },
  {
    number: 4,
    color: "#F6E05E",
    title: "Klage einreichen (Arbeitsgericht)",
    text: "Das Arbeitsgericht ist für arbeitsrechtliche Mobbing-Fälle zuständig. Im ersten Rechtszug trägt jede Partei ihre Kosten selbst — kein Kostenrisiko.",
    link: null
  },
];

const COSTS = [
  { item: "Erstberatung Anwalt", cost: "0–190 €", note: "Oft kostenlos bei Gewerkschaft" },
  { item: "Arbeitsgericht 1. Instanz", cost: "Keine Anwaltskosten", note: "Jede Partei trägt eigene Kosten" },
  { item: "Rechtsschutzversicherung", cost: "0 €", note: "Wenn vorhanden, alles abgedeckt" },
  { item: "Prozesskostenhilfe", cost: "0 €", note: "Bei geringem Einkommen möglich" },
];

const DEADLINES = [
  { action: "Kündigungsschutzklage", frist: "3 Wochen", note: "Nach Zugang der Kündigung" },
  { action: "Schadensersatz (BGB)", frist: "3 Jahre", note: "Ab Kenntnis des Schadens" },
  { action: "Lohnfortzahlung einfordern", frist: "Sofort", note: "Bei Arbeitsunfähigkeit" },
  { action: "Beschwerde beim BR", frist: "Keine Frist", note: "So früh wie möglich" },
];

export default function Anwalt() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO
        title="Rechtliche Schritte bei Mobbing: Anwalt, Klage & Schadensersatz"
        description="Welche rechtlichen Schritte gibt es bei Mobbing? Unterlassung, Schadensersatz, Arbeitsgericht. Wann brauche ich einen Anwalt? Kostenlos informieren."
        path="/anwalt"
        schema={SCHEMA}
      />

      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#BDE0FE]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Scale className="w-10 h-10 text-[#1A365D]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Rechtliche Schritte bei Mobbing
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Mobbing ist kein Kavaliersdelikt — es ist oft eine Verletzung deiner Persönlichkeitsrechte. Hier erfährst du, welche rechtlichen Möglichkeiten du hast und wann ein Anwalt sinnvoll ist.
            </p>
          </div>

          {/* Important note */}
          <div className="bg-[#FFF5F7] border border-[#FFC4D6] rounded-2xl p-6 mb-10 flex gap-4">
            <AlertCircle className="w-6 h-6 text-[#C53030] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-[#C53030] mb-1">Hinweis: Kein Rechtsrat</p>
              <p className="text-[#4A5568] text-sm">
                Dieser Ratgeber bietet allgemeine Informationen — keine Rechtsberatung. Für deinen konkreten Fall wende dich an einen Fachanwalt für Arbeitsrecht oder deine Gewerkschaft.
              </p>
            </div>
          </div>

          {/* Legal options */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Deine rechtlichen Möglichkeiten
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {LEGAL_OPTIONS.map((opt, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-2xl">{opt.emoji}</span>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-[#2D3748] text-sm">{opt.title}</h3>
                        {opt.fast && <span className="text-xs bg-[#A0E8AF]/30 text-[#1A4731] px-2 py-0.5 rounded-full font-semibold">Schnell möglich</span>}
                      </div>
                      <p className="text-xs text-[#A0AEC0] mt-0.5">{opt.when}</p>
                    </div>
                  </div>
                  <p className="text-[#718096] text-sm">{opt.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Steps */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Die richtige Reihenfolge
            </h2>
            <div className="space-y-4">
              {STEPS.map((step, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 flex gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white" style={{ backgroundColor: step.color }}>
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#2D3748] mb-1">{step.title}</h3>
                    <p className="text-[#718096] text-sm mb-2">{step.text}</p>
                    {step.link && (
                      <Link to={step.link.path} className="inline-flex items-center gap-1 text-[#1A4731] text-xs font-bold hover:underline">
                        {step.link.label}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Costs */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Was kostet das?
            </h2>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-3 bg-gray-50 px-5 py-3 text-xs font-bold text-[#718096] uppercase">
                <span>Maßnahme</span>
                <span>Kosten</span>
                <span>Hinweis</span>
              </div>
              {COSTS.map((c, i) => (
                <div key={i} className={`grid grid-cols-3 px-5 py-4 text-sm ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                  <span className="font-medium text-[#2D3748]">{c.item}</span>
                  <span className="text-[#1A4731] font-bold">{c.cost}</span>
                  <span className="text-[#A0AEC0] text-xs">{c.note}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Deadlines */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Wichtige Fristen
            </h2>
            <div className="space-y-3">
              {DEADLINES.map((d, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 flex justify-between items-center gap-4">
                  <span className="font-medium text-[#2D3748] text-sm">{d.action}</span>
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-[#C53030] text-sm">{d.frist}</div>
                    <div className="text-[#A0AEC0] text-xs">{d.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#EBF8FF] to-[#E6FFFA] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Erst dokumentieren, dann handeln
            </h2>
            <p className="text-[#718096] mb-6 max-w-xl mx-auto">
              Rechtliche Schritte stehen und fallen mit der Dokumentation. Erstelle jetzt ein Protokoll — anonym und kostenlos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/melden" className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#86D696] transition-all">
                <FileText className="w-5 h-5" />
                Jetzt dokumentieren
              </Link>
              <Link to="/mobbing-beweise" className="inline-flex items-center justify-center gap-2 border-2 border-[#BDE0FE] text-[#1A365D] font-bold px-8 py-4 rounded-full hover:bg-[#BDE0FE]/20 transition-all">
                Beweise richtig sichern →
              </Link>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
